import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <span className="font-heading text-xl font-semibold text-foreground-950 tracking-tight">
              Sugargoo<span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap font-medium"
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line text-base"></i>
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden shadow-lg animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-background-100">
                          <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </a>

            <Link
              to="/article/sugargoo-spreadsheet-guide"
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Complete Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-[fadeIn_0.15s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-4-line text-base text-primary-600"></i>
              </span>
              Home
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-compass-3-line text-base text-accent-700"></i>
              </span>
              Shopping Guide
            </a>

            <div className="pt-2 pb-1">
              <button
                onClick={() => setIsMobileCategoriesOpen(!isMobileCategoriesOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200">
                    <i className="ri-grid-line text-base text-foreground-600"></i>
                  </span>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileCategoriesOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isMobileCategoriesOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-background-200 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden bg-background-100">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/sugargoo-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100">
                <i className="ri-book-open-line text-base text-secondary-700"></i>
              </span>
              Complete Guide
            </Link>

            <div className="border-t border-background-200/70 my-2 pt-3">
              <p className="px-3 py-1 text-xs text-foreground-500 font-semibold uppercase tracking-wider mb-1">
                SEO Articles
              </p>
              <Link
                to="/article/sugargoo-review"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                  <i className="ri-star-line text-sm text-foreground-600"></i>
                </span>
                Sugargoo Review
              </Link>
              <Link
                to="/article/sugargoo-shipping-cost"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                  <i className="ri-ship-line text-sm text-foreground-600"></i>
                </span>
                Shipping Cost Guide
              </Link>
              <Link
                to="/article/is-sugargoo-legit"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                  <i className="ri-shield-check-line text-sm text-foreground-600"></i>
                </span>
                Is Sugargoo Legit?
              </Link>
              <Link
                to="/article/sugargoo-coupon"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                  <i className="ri-coupon-line text-sm text-foreground-600"></i>
                </span>
                Coupons &amp; Deals
              </Link>
              <Link
                to="/article/sugargoo-saving-tips"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                  <i className="ri-money-dollar-circle-line text-sm text-foreground-600"></i>
                </span>
                Saving Tips
              </Link>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center w-full mt-3 px-5 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Buy It Now &rarr;
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}